import React, { FunctionComponent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { programFilterOptions } from './AccessProgramsContainer.models'
import { ClinTheme } from '../../ClinTheme'
import { ClinPageContentFrame } from '../../components/ClinPageContentFrame'
import {
  IPagination,
  getCurrentPage,
  getTotalPages
} from '../../components/ClinPagination/ClinPagination.model'
import { ClinSpacer } from '../../components/ClinSpacer'
import { ProgramsTable } from '../../features/ProgramsTable'
import { IProgramSummary } from '../../types'
import { CountryDto } from '../../types/swaggerTypes'
import {
  StyledPageHeader,
  StyledPageTitle,
  StyledProgramsSearchHeader,
  StyledHeaderClinPageContentFrame,
  ProgramsPageContent,
  StyledBasicSearch,
  StyledTooltip
} from './AccessPrograms.styles'
import { ClinIcon } from '../../components/ClinIcon'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'
import { Tooltip } from 'react-tippy'
import {
  StyledFilter,
  StyledFilterWrapper,
  StyledProductSearchPagination
} from '../Products/ProductSearch/ProductSearch.styles'
import { ClinText } from '../../components/ClinText'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import { ClinChip } from '../../components/ClinChip'
import { ChipState } from '../../components/ClinChip/ClinChip.styles'

interface IAccessProgramsProps {
  /** Whether we are loading or not */
  isLoading: boolean
  /** Set of pagination*/
  pagination: IPagination
  /** Selected type of programs*/
  programs: IProgramSummary[]
  /** If the user is MA only or not */
  isMaUser?: boolean
  /** Supplied contact data */
  supportContact?: CountryDto
  /** User country code */
  userCountry?: string
  /** Selected filter options for chip-based filters */
  selectedFilterOptions?: string[]
  initialQuery?: string
  /** Therapeutic area options from API facets */
  therapeuticAreaOptions?: Array<{ label: string; value: string }>
  /** Selected therapeutic areas */
  selectedTherapeuticAreas?: Array<{ label: string; value: string }>
  /** Whether therapeutic areas filter is expanded */
  isTherapeuticAreasExpanded?: boolean
  /**A Function that runs when user clicks on the page*/
  handlePageClicked: (selectedPageIndex: number) => void
  /**A Function that runs when user click on the table row*/
  handleRowClicked: (selectedProgramId: number) => void
  /**A Function that runs when user change a page size*/
  handlePageSizeChange: (pageSize: number) => void
  /** When a search query is entered and ENTER pressed */
  handleOnSearch?: (query: string) => void
  /** When a filter chip is clicked */
  handleSetSelectedFilterOption?: (filterKey: string) => void
  /** When therapeutic areas are selected */
  handleTherapeuticAreasChange?: (
    selectedOptions: Array<{ label: string; value: string }>
  ) => void
  /** Toggle therapeutic areas expanded/collapsed */
  handleToggleTherapeuticAreasExpanded?: () => void
  /** When a therapeutic area chip is clicked */
  handleTherapeuticAreaChipClick?: (therapeuticArea: string) => void
}

export const AccessPrograms: FunctionComponent<IAccessProgramsProps> = ({
  isLoading,
  programs,
  pagination,
  isMaUser,
  supportContact,
  selectedFilterOptions,
  initialQuery,
  therapeuticAreaOptions,
  selectedTherapeuticAreas,
  isTherapeuticAreasExpanded,
  handlePageClicked,
  handleRowClicked,
  handlePageSizeChange,
  handleOnSearch,
  handleSetSelectedFilterOption,
  handleToggleTherapeuticAreasExpanded,
  handleTherapeuticAreaChipClick
}) => {
  const { t } = useTranslation()
  const [isMobile, setIsMobile] = React.useState<boolean>(false)

  const updateIsMobile = () => {
    setIsMobile(
      (window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth) <= ClinTheme.breakpoints[2]
    )
  }

  const listener = () => {
    updateIsMobile()
  }

  useEffect(() => {
    updateIsMobile()
    window.addEventListener('resize', listener)
    return () => {
      window.removeEventListener('resize', listener)
    }
  }, [isMobile])

  return (
    <ClinPageContentFrame className="access-programs" isFullWidth={true}>
      <StyledPageHeader>
        <StyledHeaderClinPageContentFrame>
          <StyledProgramsSearchHeader>
            <StyledPageTitle>{t('access_programs:title')}</StyledPageTitle>
            <StyledTooltip>
              <Tooltip
                theme="light"
                position="bottom"
                arrow={true}
                title={t('access_programs:tooltip_text')}
              >
                <ClinIcon
                  viewBox="0 0 24 24"
                  iconSize={24}
                  iconFill={ClinTheme.colors.primary}
                  iconName={ClinIconPathName.NewInformationOutline}
                />
              </Tooltip>
            </StyledTooltip>
            <StyledBasicSearch
              placeholder={t('access_programs:search_placeholder_new')}
              debounceTimeMs={500}
              handleOnChange={(query) =>
                handleOnSearch && handleOnSearch(query)
              }
            />
          </StyledProgramsSearchHeader>
        </StyledHeaderClinPageContentFrame>
      </StyledPageHeader>
      <ProgramsPageContent>
        <ClinPageContentFrame
          isFullWidth={false}
          className="access-programs-container-inner"
        >
          <ClinSpacer height={ClinTheme.space[4]} />

          <StyledFilterWrapper>
            <StyledFilter>
              <ClinText
                marginRight={`${ClinTheme.space[3]}px`}
                lineHeight={ClinTheme.lineHeights.small}
                color={ClinTheme.colors.black}
                fontSize={ClinTheme.fontSizes[1]}
                fontWeight={ClinTheme.fontWeights.bold}
              >
                Filter by
              </ClinText>
              {programFilterOptions?.map((filter, index) => {
                const isActive =
                  (selectedFilterOptions?.findIndex(
                    (i: any) => i === filter.filterKey
                  ) ?? -1) > -1

                const handleClick = () => {
                  handleSetSelectedFilterOption?.(filter.filterKey)

                  analyticsServiceSingleton.trackEvent(filter.event, {
                    filterId: filter.optionId,
                    filterName: filter.mixPanelLabel,
                    isActive: !isActive
                  })
                }

                return (
                  <ClinChip
                    style={{ padding: '8px 16px', margin: 0 }}
                    key={index}
                    state={isActive ? ChipState.Active : ChipState.Default}
                    isInteractive={true}
                    onClick={handleClick}
                  >
                    {filter.optionValue}
                  </ClinChip>
                )
              })}

              {/* Therapeutic Area Filter */}
              {!isTherapeuticAreasExpanded && (
                <ClinChip
                  style={{ padding: '8px 16px', margin: 0 }}
                  state={
                    selectedTherapeuticAreas &&
                    selectedTherapeuticAreas.length > 0
                      ? ChipState.Active
                      : ChipState.Default
                  }
                  isInteractive={true}
                  onClick={handleToggleTherapeuticAreasExpanded}
                >
                  Therapeutic Area
                  {selectedTherapeuticAreas &&
                    selectedTherapeuticAreas.length > 0 && (
                      <span style={{ marginLeft: '4px' }}>
                        ({selectedTherapeuticAreas.length})
                      </span>
                    )}
                </ClinChip>
              )}

              {/* Expanded Therapeutic Area Options */}
              {isTherapeuticAreasExpanded &&
                therapeuticAreaOptions?.map((option, index) => {
                  const isActive = selectedTherapeuticAreas?.some(
                    (ta) => ta.value === option.value
                  )

                  return (
                    <ClinChip
                      style={{ padding: '8px 16px', margin: 0 }}
                      key={`therapeutic-area-${index}`}
                      state={isActive ? ChipState.Active : ChipState.Default}
                      isInteractive={true}
                      onClick={() =>
                        handleTherapeuticAreaChipClick?.(option.value)
                      }
                    >
                      {option.label}
                    </ClinChip>
                  )
                })}

              {/* Show Less Button */}
              {isTherapeuticAreasExpanded && (
                <ClinChip
                  style={{ padding: '8px 16px', margin: 0 }}
                  state={ChipState.Default}
                  isInteractive={true}
                  onClick={handleToggleTherapeuticAreasExpanded}
                >
                  Show less ▲
                </ClinChip>
              )}
            </StyledFilter>
          </StyledFilterWrapper>

          <ClinSpacer height={ClinTheme.space[3]} />
          <ClinSpacer hasBorder={true} />
          <ClinSpacer height={ClinTheme.space[5]} />

          <ProgramsTable
            isLoading={isLoading}
            programs={programs}
            isMaUser={isMaUser}
            supportContact={supportContact}
            initialQuery={initialQuery}
            handleRowClicked={(orderId) => handleRowClicked(orderId)}
          />

          {!isLoading &&
            programs &&
            programs.length > 0 &&
            pagination &&
            pagination.total > 0 && (
              <StyledProductSearchPagination
                showLeftSpacer
                classNameForPaginationSelector="pagination-select"
                classNameForPaginationContainer="pagination-container"
                classNameForPreviousButton="pagination-previous-button"
                classNameForNextButton="pagination-next-button"
                classNameForResultsPerPage="pagination-results-per-page"
                currentPage={getCurrentPage(pagination)}
                totalPages={getTotalPages(pagination)}
                pageSize={pagination.take}
                handlePageSelected={(pageIndex) =>
                  handlePageClicked && handlePageClicked(pageIndex)
                }
                handlePageSizeChange={handlePageSizeChange}
              />
            )}
        </ClinPageContentFrame>
      </ProgramsPageContent>
    </ClinPageContentFrame>
  )
}
